/**
 * Settings Page - User preferences including color scheme and appearance mode
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, COLOR_THEMES, APPEARANCE_MODES } from '../context/ThemeContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Lock,
  Link2, 
  ArrowLeft, 
  Settings,
  Palette,
  Check,
  User,
  Bell,
  Shield,
  Moon,
  Sun,
  Monitor,
  Loader2,
  BellOff,
  BellRing,
  CreditCard
} from 'lucide-react';
import PaymentMethodsSection from '../components/PaymentMethodsSection';
import PasskeySettings from '../components/PasskeySettings';
import TrustedDevicesSettings from '../components/TrustedDevicesSettings';

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { 
    colorTheme, 
    changeColorTheme, 
    availableThemes,
    appearanceMode,
    changeAppearanceMode,
    availableModes,
    isDarkMode
  } = useTheme();
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush
  } = usePushNotifications();
  const [activeTab, setActiveTab] = useState('appearance');

  const handleThemeChange = (themeName) => {
    changeColorTheme(themeName);
    toast.success(`Theme changed to ${availableThemes[themeName].name}`);
  };

  const handleModeChange = (mode) => {
    changeAppearanceMode(mode);
    toast.success(`Appearance set to ${availableModes[mode].name}`);
  };

  const handlePushToggle = async () => {
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        toast.success('Push notifications disabled');
      } else {
        await subscribeToPush();
        toast.success('Push notifications enabled!');
      }
    } catch (error) {
      if (error.message === 'Notification permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to update notification settings');
      }
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'dark': return Moon;
      case 'light': return Sun;
      case 'system': return Monitor;
      default: return Moon;
    }
  };

  const tabs = [
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'account', label: 'Account', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'payment-methods', label: 'Payment Methods', icon: CreditCard },
    { id: 'privacy', label: 'Privacy', icon: Lock },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground hover:bg-muted"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold text-foreground tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                SETTINGS
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="md:w-48 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
                      activeTab === tab.id
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1">
            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Appearance</h2>
                  <p className="text-muted-foreground text-sm">Customize how Liantiao looks for you</p>
                </div>

                {/* Appearance Mode Selection */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                    {isDarkMode() ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-primary" />}
                    Appearance Mode
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Choose between dark mode, light mode, or follow your system settings.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(availableModes).map(([key, mode]) => {
                      const ModeIcon = getModeIcon(key);
                      return (
                        <button
                          key={key}
                          onClick={() => handleModeChange(key)}
                          className={`relative p-4 rounded-lg border transition-all ${
                            appearanceMode === key
                              ? 'border-2 border-primary bg-muted/50'
                              : 'border-border hover:border-muted-foreground hover:bg-muted/30'
                          }`}
                          data-testid={`mode-${key}`}
                        >
                          {appearanceMode === key && (
                            <div className="absolute top-2 right-2">
                              <Check className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <div className="w-12 h-12 rounded-full mb-3 mx-auto bg-muted flex items-center justify-center">
                            <ModeIcon className={`h-6 w-6 ${
                              key === 'dark' ? 'text-indigo-400' : 
                              key === 'light' ? 'text-yellow-400' : 
                              'text-muted-foreground'
                            }`} />
                          </div>
                          <p className="text-foreground text-sm font-medium text-center">{mode.name}</p>
                          <p className="text-muted-foreground text-xs text-center mt-1">{mode.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Color Theme Selection */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                    <Palette className="h-5 w-5 text-primary" />
                    Accent Color
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Choose your preferred accent color. This will be applied throughout the app.
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(availableThemes).map(([key, theme]) => (
                      <button
                        key={key}
                        onClick={() => handleThemeChange(key)}
                        className={`relative p-4 rounded-lg border transition-all ${
                          colorTheme === key
                            ? 'border-2 border-primary bg-muted/50'
                            : 'border-border hover:border-muted-foreground hover:bg-muted/30'
                        }`}
                        data-testid={`theme-${key}`}
                      >
                        {colorTheme === key && (
                          <div className="absolute top-2 right-2">
                            <Check className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div 
                          className="w-10 h-10 rounded-full mb-3 mx-auto shadow-lg"
                          style={{ backgroundColor: theme.primaryHex }}
                        />
                        <p className="text-foreground text-sm font-medium text-center">{theme.name}</p>
                        <p className="text-muted-foreground text-xs text-center mt-1">{theme.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-foreground mb-4">Preview</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    See how your selected theme looks with various UI elements.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex gap-3 flex-wrap">
                      <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                        Primary Button
                      </Button>
                      <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
                        Outline Button
                      </Button>
                      <Button variant="ghost" className="text-primary hover:bg-primary/10">
                        Ghost Button
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="px-3 py-1 rounded-full text-xs font-mono bg-primary/20 text-primary border border-primary/30">
                        Status Badge
                      </span>
                      <span className="text-primary font-medium">Accent Text</span>
                      <div className="w-4 h-4 rounded bg-primary" />
                    </div>
                    
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full w-2/3 bg-primary rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Account</h2>
                  <p className="text-muted-foreground text-sm">Manage your account settings</p>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Profile Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-border">
                      <div>
                        <p className="text-muted-foreground text-sm">Name</p>
                        <p className="text-foreground">{user?.name || 'Not set'}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-border">
                      <div>
                        <p className="text-muted-foreground text-sm">Email</p>
                        <p className="text-foreground">{user?.email || 'Not set'}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-muted-foreground text-sm">Phone</p>
                        <p className="text-foreground">{user?.phone || 'Not set'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Notifications</h2>
                  <p className="text-muted-foreground text-sm">Configure how you receive notifications</p>
                </div>

                {/* Push Notifications Card */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                    <Bell className="h-5 w-5 text-primary" />
                    Push Notifications
                  </h3>
                  
                  {!pushSupported ? (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-muted-foreground text-sm">
                        Push notifications are not supported in your browser. Try using Chrome, Firefox, or Edge.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-muted-foreground text-sm">
                        Receive browser notifications for important updates like new orders, delivery updates, and system announcements.
                      </p>
                      
                      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
                        <div className="flex items-center gap-3">
                          {pushSubscribed ? (
                            <BellRing className="h-5 w-5 text-green-500" />
                          ) : (
                            <BellOff className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div>
                            <p className="text-foreground font-medium">
                              {pushSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {pushPermission === 'denied' 
                                ? 'Blocked in browser settings' 
                                : pushSubscribed 
                                  ? 'You will receive push notifications' 
                                  : 'Enable to receive updates'
                              }
                            </p>
                          </div>
                        </div>
                        
                        <Button
                          onClick={handlePushToggle}
                          disabled={pushLoading || pushPermission === 'denied'}
                          variant={pushSubscribed ? 'outline' : 'default'}
                          className={pushSubscribed 
                            ? 'border-border text-foreground hover:bg-muted' 
                            : 'bg-primary text-primary-foreground hover:bg-primary/90'
                          }
                          data-testid="push-toggle-btn"
                        >
                          {pushLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : pushSubscribed ? (
                            'Disable'
                          ) : (
                            'Enable'
                          )}
                        </Button>
                      </div>
                      
                      {pushPermission === 'denied' && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                          <p className="text-destructive text-sm">
                            Notifications are blocked. Please update your browser settings to enable them.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Email Notifications Card */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                    <Bell className="h-5 w-5 text-primary" />
                    Email Notifications
                  </h3>
                  <p className="text-muted-foreground text-center py-4">
                    Email notification preferences coming soon
                  </p>
                </div>
              </div>
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'payment-methods' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Payment Methods</h2>
                  <p className="text-muted-foreground text-sm">Manage your saved cards for bidding</p>
                </div>
                
                <PaymentMethodsSection />
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Security</h2>
                  <p className="text-muted-foreground text-sm">Manage passkeys and trusted devices</p>
                </div>

                <PasskeySettings />
                <TrustedDevicesSettings />
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">Privacy</h2>
                  <p className="text-muted-foreground text-sm">Manage your privacy preferences</p>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted-foreground text-center py-8">
                    Privacy settings coming soon
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
